// stylePreviewRenderer — the TRUE-preview service for text styles: renders a
// style recipe onto the user's own words/font with a real fabric Textbox and
// the SAME composeRecipePatch the engine's applyTextStyle uses, so a thumb
// cannot drift from what a click paints on the canvas.
//
// - ONE shared offscreen StaticCanvas, lazily created; renders SERIALIZE
//   through an internal promise chain (strictly one at a time).
// - Output is a PNG object URL (async toBlob encode; ~5-15 KB per thumb vs
//   64 KB+ base64 dataURLs). Callers put it straight on an <img src>.
// - LRU cache (cap 600) keyed on style id (pass `cacheKey`) or a recipe hash,
//   plus text/font/weight/ground/size. Evicted or cleared entries revoke
//   their object URLs.
// - The canvas background stays TRANSPARENT — `ground` only picks the default
//   text fill when the recipe brings no fill of its own (the modal supplies
//   its own backdrop behind the <img>).
// - Fonts load through the app's fontLoader (ensureFont) before rendering;
//   dead textures fall back to a solid via composeRecipePatch — a preview
//   promise never rejects for a bad texture.

import { StaticCanvas, Textbox } from 'fabric';
import { ensureFont } from '../../fonts/fontLoader';
import { composeRecipePatch, solidFromFillValue } from './composeRecipePatch';

const CACHE_CAP = 600;

// Default text paint per preview ground when the recipe has no fill — also
// feeds the derive ctx (echo inherits it, outline auto-contrasts against it).
const DEFAULT_FILL = { light: '#1f2937', dark: '#f1f5f9' };

// ---------------------------------------------------------------- LRU cache
const cache = new Map(); // key -> object URL (Map order = LRU order)

function cacheGet(key) {
  if (!cache.has(key)) return null;
  const url = cache.get(key);
  cache.delete(key);
  cache.set(key, url); // bump to most-recent
  return url;
}

function cachePut(key, url) {
  if (cache.has(key)) {
    const old = cache.get(key);
    cache.delete(key);
    if (old !== url) { try { URL.revokeObjectURL(old); } catch { /* gone */ } }
  }
  cache.set(key, url);
  while (cache.size > CACHE_CAP) {
    const [oldestKey, oldestUrl] = cache.entries().next().value;
    cache.delete(oldestKey);
    try { URL.revokeObjectURL(oldestUrl); } catch { /* gone */ }
  }
}

/** djb2 over the recipe JSON — only used when the caller passes no cacheKey. */
function hashStr(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

function keyFor({ recipe, text, fontFamily, fontWeight, ground, width, height, fontSize, cacheKey, baseFill }) {
  const id = cacheKey ?? `r${hashStr(JSON.stringify(recipe ?? null))}`;
  return `${id}|${text ?? ''}|${fontFamily ?? ''}|${fontWeight ?? ''}|${ground ?? 'light'}|${width}x${height}|${fontSize ?? 'auto'}|${baseFill ?? ''}`;
}

// ------------------------------------------------------------- shared canvas
let sharedCanvas = null;

function getCanvas(width, height) {
  if (!sharedCanvas) {
    sharedCanvas = new StaticCanvas(undefined, {
      width,
      height,
      renderOnAddRemove: false,
      enableRetinaScaling: false,
    });
  } else {
    sharedCanvas.setDimensions({ width, height });
  }
  return sharedCanvas;
}

// One render at a time — the canvas is shared. The chain itself never
// rejects (same discipline as the engine's chainedTextFx); callers get the
// real call's promise.
let queue = Promise.resolve();
function enqueue(run) {
  const p = queue.then(run, run);
  queue = p.then(() => undefined, () => undefined);
  return p;
}

function toBlob(canvasEl) {
  return new Promise((resolve, reject) => {
    canvasEl.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('preview toBlob returned null'))),
      'image/png',
    );
  });
}

const numericWeight = (w) => {
  if (typeof w === 'number') return w;
  if (w === 'bold') return 700;
  if (w === 'normal') return 400;
  const n = parseInt(w, 10);
  return Number.isFinite(n) ? n : null;
};

// ------------------------------------------------------------------- render
async function renderOnce({ recipe, text, fontFamily, fontWeight, ground, width, height, fontSize, baseFill }) {
  // baseFill: the caller's real text color — recipes that keep the fill
  // ("kept") preview honestly instead of colliding with the ground default
  // (e.g. black shadow over the near-black light-ground default).
  const defaultFill = baseFill || DEFAULT_FILL[ground === 'dark' ? 'dark' : 'light'];

  // Fonts first: canvas text does not trigger CSS font loading by itself.
  // ensureFont is memoized per family and soft-fails ({ok:false}) — a missing
  // font must never fail the preview (fabric falls back).
  if (fontFamily) {
    const w = numericWeight(fontWeight);
    try { await ensureFont(fontFamily, w ? [w] : undefined); } catch { /* soft */ }
  }

  const canvas = getCanvas(width, height);

  // Single line, fontSize auto-fit: start at ~60% of height, then shrink to
  // the available width. Fit runs BEFORE compose so the effects derive at the
  // final fontSize (they are fontSize-relative).
  const line = String(text ?? 'Aa').split('\n')[0].trim() || 'Aa';
  let fs = fontSize ?? Math.max(8, Math.round(height * 0.6));
  const box = new Textbox(line, {
    width: width * 10, // never wrap — the preview is one line
    fontSize: fs,
    fontFamily: fontFamily || 'Arial',
    fontWeight: fontWeight ?? 'normal',
    fill: defaultFill,
    textAlign: 'center',
    originX: 'center',
    originY: 'center',
    left: width / 2,
    top: height / 2,
  });
  box.initDimensions?.();
  const maxW = width - 16;
  const textW = (typeof box.calcTextWidth === 'function' && box.calcTextWidth()) || box.width;
  if (textW > maxW) {
    fs = Math.max(6, fs * (maxW / textW));
    box.set({ fontSize: fs });
    box.initDimensions?.();
  }
  // Hug the box to the measured text: percentage gradients (and per-line
  // highlight blocks) span the OBJECT box — the oversized never-wrap box made
  // a rainbow gradient show only its middle slice.
  const hugW = ((typeof box.calcTextWidth === 'function' && box.calcTextWidth()) || box.width) + 4;
  box.set({ width: hugW });
  box.initDimensions?.();

  // The engine-shared forward half: resolve fill, derive effects, compose
  // highlight — all at the fitted fontSize. Textures resolve async in here.
  const patch = await composeRecipePatch(recipe ?? {}, {
    fontSize: fs,
    ctx: { fillColor: defaultFill },
    currentFill: defaultFill, // hollow with no recipe fill outlines in the default
  });

  if (patch.fill.applied) {
    box.set('fill', patch.fill.paint);
  } else if (patch.fill.failed) {
    // Dead texture: solid fallback — never a rejected preview.
    box.set('fill', solidFromFillValue(patch.fill.value) ?? defaultFill);
  }
  box.set(patch.props); // AFTER the fill — hollow's fill:'transparent' must win
  box.set({ textBackgroundColor: patch.textBackgroundColor });
  box.initDimensions?.();
  box.setCoords();

  canvas.add(box);
  canvas.renderAll();
  try {
    const blob = await toBlob(canvas.lowerCanvasEl);
    return URL.createObjectURL(blob);
  } finally {
    canvas.remove(box);
  }
}

// ---------------------------------------------------------------- public API

/**
 * Render a style recipe onto the user's text → PNG object URL.
 *
 * @param {object} opts
 *   recipe      — textPresets-shaped recipe ({ fill?, effects?, highlight? })
 *   text        — the user's words (first line is used)
 *   fontFamily  — the user's font (loaded via ensureFont before rendering)
 *   fontWeight  — 'normal' | 'bold' | number
 *   ground      — 'light' | 'dark': picks the default fill when the recipe
 *                 has none; the canvas itself stays transparent
 *   width       — thumb width in px (default 200)
 *   height      — thumb height in px (default 80)
 *   fontSize    — override the auto-fit (~60% of height, width-shrunk)
 *   cacheKey    — stable style id for the cache key (avoids JSON hashing)
 *   baseFill    — the caller's real text color: recipes that keep the fill
 *                 preview honestly instead of using the ground default
 * @returns {Promise<string>} blob: object URL (cache-owned; see releasePreview)
 */
export function renderStylePreview({
  recipe,
  text,
  fontFamily,
  fontWeight,
  ground = 'light',
  width = 200,
  height = 80,
  fontSize,
  cacheKey,
  baseFill,
} = {}) {
  const opts = { recipe, text, fontFamily, fontWeight, ground, width, height, fontSize, cacheKey, baseFill };
  const key = keyFor(opts);
  const hit = cacheGet(key);
  if (hit) return Promise.resolve(hit); // sync fast path — no queue hop
  return enqueue(async () => {
    const raced = cacheGet(key); // an identical request ahead of us in the queue
    if (raced) return raced;
    const url = await renderOnce(opts);
    cachePut(key, url);
    return url;
  });
}

/**
 * Square SWATCH variant (the sidebar's compact featured grid): the same
 * pipeline on a SHORT "Aa" sample and a square canvas — a dedicated variant
 * instead of letting callers squash the wide preview (the auto-fit already
 * handles a 1:1 aspect; distorting a wide render would). Same LRU cache,
 * same baseFill contract; pass a distinct cacheKey (e.g. `swatch|${id}`) so
 * square and wide renders of one style never collide.
 *
 * @param {object} opts  recipe / fontFamily / fontWeight / ground / baseFill /
 *   cacheKey as renderStylePreview; `size` = square edge px (default 96).
 * @returns {Promise<string>} blob: object URL (cache-owned)
 */
export function renderStyleSwatch({ size = 96, ...opts } = {}) {
  return renderStylePreview({
    ...opts,
    text: 'Aa',
    width: size,
    height: size,
    // ~46% of the edge: "Aa" plus effect spill (long shadows, echo) stays
    // inside the square without the width-shrink pass kicking in.
    fontSize: Math.round(size * 0.46),
  });
}

/**
 * Release a preview URL. URLs still owned by the cache are left alone (the
 * cache revokes on eviction/clear); anything already evicted is revoked now.
 */
export function releasePreview(url) {
  if (!url) return;
  for (const owned of cache.values()) {
    if (owned === url) return;
  }
  try { URL.revokeObjectURL(url); } catch { /* already gone */ }
}

/** Drop every cached preview and revoke all object URLs. */
export function clearPreviewCache() {
  for (const url of cache.values()) {
    try { URL.revokeObjectURL(url); } catch { /* already gone */ }
  }
  cache.clear();
}
